import React from 'react';
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../../modules/subscription/application/useSubscription';
import { useBreakpoint } from '../../../hooks/useBreakpoint';

interface InteractiveTrainingTourProps {
  run: boolean;
  onTourComplete: () => void;
}

const TourTooltip = ({
  index,
  size,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}: TooltipRenderProps) => {
  const { t } = useTranslation();

  return (
    <div 
      {...tooltipProps} 
      className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-[28px] p-6 shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-white/20 dark:border-white/5 font-sans max-w-[420px] w-[calc(100vw-2rem)] sm:w-auto"
    >
      <div className="flex flex-col h-full">
        {step.title && (
          <h3 className="text-[19px] font-semibold mb-2 text-content-primary tracking-tight">
            {step.title}
          </h3>
        )}
        <div className="text-[15px] leading-relaxed text-gray-600 dark:text-gray-300 mb-8">
          {step.content}
        </div>
        
        <div className="flex flex-wrap items-center justify-between gap-y-4 mt-auto pt-4 border-t border-gray-100 dark:border-white/5">
          <div className="flex items-center">
            {index === 0 && (
              <button 
                {...skipProps} 
                className="text-[13px] font-medium text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                {t('common.maybeLater')}
              </button>
            )}
            {index > 0 && (
              <div className="flex gap-1.5 items-center">
                {Array.from({ length: size }).map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-1.2 rounded-full transition-all duration-300 ${i === index ? 'w-4 bg-violet-500' : 'w-1.2 bg-gray-200 dark:bg-gray-700'}`}
                  />
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            {index > 0 && (
              <button 
                {...backProps} 
                className="text-[14px] font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                {t('common.previous')}
              </button>
            )}
            <button 
              {...primaryProps} 
              className="text-[13px] sm:text-[14px] font-semibold text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:-translate-y-0.5 text-center min-w-[80px]"
            >
              {isLastStep ? t('common.completed') : (
                <span className="flex items-center gap-1">
                  <span>{t('common.next')}</span>
                  <span className="hidden sm:inline opacity-80 font-normal">
                    ({index + 1}/{size})
                  </span>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const InteractiveTrainingTour: React.FC<InteractiveTrainingTourProps> = ({ run, onTourComplete }) => {
  const { t } = useTranslation();
  const { isPremium } = useSubscription();
  const { isMobile, isTablet, breakpoint } = useBreakpoint();
  const isSmallScreen = isMobile || isTablet || breakpoint === 'lg';

  const baseSteps: Step[] = [
    {
      target: 'body',
      placement: 'center',
      title: t('tour.welcome.title'),
      content: t('tour.welcome.content'),
      disableBeacon: true,
    }
  ];

  // For Basic users on mobile, we show Home first
  if (!isPremium && isSmallScreen) {
    baseSteps.push({
      target: '[data-tour="sidebar-home"]',
      content: t('tour.home'),
      placement: (isSmallScreen ? 'top' : 'right') as 'top' | 'right',
    });
  }

  baseSteps.push({
    target: '[data-tour="sidebar-patients"]',
    content: t('tour.patients'),
    placement: (isSmallScreen ? 'top' : 'right') as 'top' | 'right',
  });
  // Sessions & Therapeutic Plans steps removed — those items are no longer in
  // the main menu (accessed from the patient view).

  if (isSmallScreen && isPremium) {
    baseSteps.push({
      target: '[data-tour="mobile-record-live"]',
      content: t('tour.mobileRecordLive'),
      placement: 'top',
    });
  }

  const steps: Step[] = [
    ...baseSteps,
    {
      target: '[data-tour="sidebar-settings"]',
      content: t('tour.settings'),
      placement: (isSmallScreen ? 'top' : 'right') as 'top' | 'right',
    },
    {
      target: '[data-tour="notifications"]',
      content: t('tour.notifications'),
      placement: 'bottom' as const,
    },
    {
      target: '[data-tour="user-profile"]',
      content: t('tour.profile'),
      placement: 'bottom' as const,
    }
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      onTourComplete();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      showProgress={false}
      showSkipButton={true}
      callback={handleJoyrideCallback}
      tooltipComponent={TourTooltip}
      disableScrolling={false}
      scrollOffset={100}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: 'rgba(255, 255, 255, 0.95)',
          overlayColor: 'rgba(0, 0, 0, 0.4)',
        },
        spotlight: {
          borderRadius: 20,
        }
      }}
    />
  );
};
